import { useCalculateAgendaRecordMutation } from 'store/services/flow/agendaRecords';
import { useCallback, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import type { InlineRecordType, RecordFormItem } from 'types';
import { VISIBILITY_STATE } from 'types/enums';

import {
  fixAttributeRecordIds,
  getAttributeForCalculate,
  mapCalculateResponseToFormValues,
  NEW_RECORD_PREFIX,
} from 'utils/agendaRecord/agendaRecord.ts';
import { calculateResponseSchema } from 'utils/validations/useCalculateResponseSchema';
import { isIdObjectArray } from '../../typeChecks.ts';
import type { FormItemUiDefinition } from 'types/formItem';

export type UpdatedItems = Array<{ item: RecordFormItem; sectionId?: string }>;
interface UseRecordCalculationProps {
  agendaId: string;
  recordId: string;
  items: RecordFormItem[];
  form: UseFormReturn;
  onLoadingChange?: (loading: boolean) => void;
  updateItems?: (updatedItems: UpdatedItems) => void;
}

export const useRecordCalculation = ({ agendaId, recordId, items, form, onLoadingChange, updateItems }: UseRecordCalculationProps) => {
  const { t } = useTranslation();
  const [calculateAgendaRecord] = useCalculateAgendaRecordMutation();
  const [inlineRecordDataState, setInlineRecordDataState] = useState<InlineRecordType[]>([]);

  const mergeInlineData = useCallback(
    (nestedAttrs?: InlineRecordType[]) => {
      if (nestedAttrs && nestedAttrs.length > 0) {
        setInlineRecordDataState(nestedAttrs);
        return nestedAttrs;
      }
      return inlineRecordDataState.length ? inlineRecordDataState : undefined;
    },
    [inlineRecordDataState]
  );

  const updateValues = useCallback(
    (
      key: string,
      value: string | string[] | { id: string }[],
      matchingItem?: RecordFormItem,
      visibilityFormItemUiDefinition?: FormItemUiDefinition | null
    ) => {
      const val = form.getValues(key);

      if (!Array.isArray(val)) {
        form.setValue(key, value, { shouldDirty: true });
        if (matchingItem && visibilityFormItemUiDefinition !== undefined) {
          matchingItem.visibilityFormItemUiDefinition = visibilityFormItemUiDefinition;
          return true;
        }
        return;
      }

      const newFilteredValues: string[] | { id: string }[] = isIdObjectArray(val)
        ? val.filter(({ id }) => id.startsWith(NEW_RECORD_PREFIX))
        : val.filter((v) => v?.startsWith && v.startsWith(NEW_RECORD_PREFIX));

      const newValueIds: string[] = isIdObjectArray(newFilteredValues) ? newFilteredValues.map(({ id }) => id) : newFilteredValues;

      const valueArray = Array.isArray(value) ? value : [value];
      const filteredValues = isIdObjectArray(valueArray)
        ? valueArray.filter(({ id }) => !newValueIds.includes(`${NEW_RECORD_PREFIX}${id}`))
        : valueArray.filter((v) => !newValueIds.includes(`${NEW_RECORD_PREFIX}${v}`));

      form.setValue(key, [...newFilteredValues, ...filteredValues], { shouldDirty: true });

      if (matchingItem && visibilityFormItemUiDefinition !== undefined) {
        matchingItem.visibilityFormItemUiDefinition = visibilityFormItemUiDefinition;
        return true;
      }
    },
    [form]
  );

  const handleCalculate = useCallback(
    async (nestedListAttributes?: InlineRecordType[]) => {
      const inlineData = mergeInlineData(nestedListAttributes);
      const fixedNestedListAttributes = fixAttributeRecordIds(inlineData);

      try {
        onLoadingChange?.(true);

        const values = form.getValues();
        const attributes = items?.map(({ formItem }) => {
          const value = values[formItem.id];
          const type = formItem.definition.type;

          return getAttributeForCalculate(type, formItem.id, value, formItem.definition);
        });

        const body = {
          recordId: !recordId.startsWith('new') ? recordId : null,
          attributes: attributes.filter((attr) => !!attr),
          ...(fixedNestedListAttributes.length && { inlineRecordData: fixedNestedListAttributes }),
        };

        const res = await calculateAgendaRecord({
          agendaId,
          body,
        });

        if ('data' in res) {
          const validatedData = await calculateResponseSchema.validate(res.data);
          const updatedValues = mapCalculateResponseToFormValues(validatedData.attributes);
          const updatedItems: UpdatedItems = [];

          Object.entries(updatedValues).forEach(([key, value]) => {
            const matchingItem = items.find(({ formItem }) => formItem.id === key);
            const matchingItemCopy = matchingItem ? { ...matchingItem } : undefined;
            const attribute = validatedData.attributes.find(({ formItem }) => formItem?.id === key);

            const updated = updateValues(key, value, matchingItemCopy, attribute?.visibility?.formItemUiDefinition);
            if (updated && matchingItemCopy) {
              matchingItemCopy.visibilityState = attribute?.visibility?.visibilityState ?? attribute?.state ?? matchingItemCopy.visibilityState;
              matchingItemCopy.visibilityMultipleUiDefinitions = attribute?.visibility?.multipleUiDefinitions ?? false;
              updatedItems.push({ item: matchingItemCopy, sectionId: attribute?.sectionId });
            } else {
              if (attribute && attribute.formItem) {
                const recordFormItem: RecordFormItem = {
                  formItem: attribute.formItem,
                  visibilityState: attribute.visibility?.visibilityState ?? attribute.state ?? VISIBILITY_STATE.EDITABLE,
                  attribute,
                  visibilityFormItemUiDefinition: attribute.visibility?.formItemUiDefinition,
                  visibilityMultipleUiDefinitions: attribute.visibility?.multipleUiDefinitions ?? false,
                };
                updatedItems.push({ item: recordFormItem, sectionId: attribute.sectionId });
              }
            }
          });

          if (updatedItems.length) {
            updateItems?.(updatedItems);
          }
        } else {
          toast.error(t('toast.calculated.error'));
        }
      } catch (error) {
        toast.error(t('toast.calculated.error'));
        throw error;
      } finally {
        onLoadingChange?.(false);
      }
    },
    [mergeInlineData, onLoadingChange, form, items, recordId, calculateAgendaRecord, agendaId, updateValues, updateItems, t]
  );

  return {
    handleCalculate,
  };
};
